import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import BookingWidget from "./BookingWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Personal Brand Strategy",
  "Content Production",
  "Social Media Growth",
  "Paid Ads & Monetisation",
  "Full Studio Engagement",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: projectTypes[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short note.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        project_type: form.project_type,
        message: form.message.trim(),
      });
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        project_type: projectTypes[0],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Could not send. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        // FastAPI validation error array
        const field = detail[0]?.loc?.slice(-1)?.[0];
        msg = field ? `${detail[0].msg} (${field})` : detail[0].msg;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 lg:py-36 bg-[var(--lt-charcoal)] text-[var(--lt-peace)] lt-grain"
    >
      <div className="container mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-12 gap-6 mb-16 lg:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-5" style={{ color: "#D3B89D" }}>/ Begin</p>
              <h2
                data-testid="contact-headline"
                className="font-display tracking-editorial leading-[1.02] text-[var(--lt-peace)]"
                style={{ fontSize: "clamp(2.4rem, 5vw, 4.8rem)", fontWeight: 600 }}
              >
                Let's build something
                <br />
                <em className="italic text-[var(--lt-clay)]">worth paying attention to.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9 flex lg:items-end">
            <Reveal delay={160}>
              <p className="text-[var(--lt-clay)]/80 text-base lg:text-lg max-w-sm">
                Book a discovery call or send a note. We respond within one business day.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Booking column */}
          <Reveal className="col-span-12 lg:col-span-7 lg:border-r lg:border-[rgba(211,184,157,0.18)] lg:pr-12">
            <p className="eyebrow mb-6" style={{ color: "#D3B89D" }}>
              01 / Book a Discovery Call
            </p>
            <p className="text-[var(--lt-clay)]/90 leading-relaxed mb-8 max-w-md">
              A 30 minute call to understand your goals and whether the studio is the right
              partner. No pitch deck. Just clarity.
            </p>

            <BookingWidget />

            <div className="mt-10 space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--lt-clay)]/60">
                Direct
              </p>
              <a
                href="mailto:Mehak@littlethings.ltd"
                data-testid="contact-email"
                className="font-display italic text-[var(--lt-peace)] hover:text-[var(--lt-clay)] transition-colors block"
                style={{ fontSize: "1.4rem" }}
              >
                Mehak@littlethings.ltd
              </a>
            </div>
          </Reveal>

          {/* Form column */}
          <Reveal delay={140} className="col-span-12 lg:col-span-5">
            <p className="eyebrow mb-6" style={{ color: "#D3B89D" }}>
              02 / Send an Inquiry
            </p>

            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="grid grid-cols-2 gap-x-6 gap-y-2"
            >
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--lt-clay)]/70">
                  Your Name
                </label>
                <input
                  data-testid="contact-input-name"
                  className="lt-field lt-field--dark"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--lt-clay)]/70">
                  Email
                </label>
                <input
                  type="email"
                  data-testid="contact-input-email"
                  className="lt-field lt-field--dark"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@brand.com"
                  required
                />
              </div>
              <div className="col-span-2 sm:col-span-1 mt-4">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--lt-clay)]/70">
                  Company / Brand
                </label>
                <input
                  data-testid="contact-input-company"
                  className="lt-field lt-field--dark"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Your company or brand"
                />
              </div>
              <div className="col-span-2 sm:col-span-1 mt-4">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--lt-clay)]/70">
                  Project Type
                </label>
                <select
                  data-testid="contact-input-project"
                  className="lt-field lt-field--dark"
                  value={form.project_type}
                  onChange={update("project_type")}
                >
                  {projectTypes.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 mt-4">
                <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--lt-clay)]/70">
                  Tell us about your goal
                </label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  className="lt-field lt-field--dark resize-none"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A short paragraph is plenty."
                  required
                />
              </div>
              <div className="col-span-2 mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[var(--lt-clay)]/60 max-w-xs">
                  By submitting you agree to be contacted about your inquiry.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 bg-[var(--lt-peace)] text-[var(--lt-charcoal)] px-7 py-4 text-xs uppercase tracking-[0.24em] hover:bg-[var(--lt-clay)] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Inquiry"}
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
